/**
 * Binding Editor for re-pointing an existing mapping's source and target.
 * Shows a collapsed summary or an expanded editor with source/target dropdowns.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { FieldMapping, MappingResponse } from '../../services/api';
import { useFieldValidation } from '../hooks/useFieldValidation';
import InlineValidationWarnings from '../validation/InlineValidationWarnings';
import type { TypeCompatibilityIssue, SuggestedFix } from '../validation/typeCompatibility';

interface SourceTargetOptions {
  files: string[];
  fieldsByFile: Record<string, string[]>;
}

interface TargetOptions {
  tables: string[];
  fieldsByTable: Record<string, string[]>;
}

export interface BindingEditorProps {
  mapping: FieldMapping;
  mappingResponse?: MappingResponse;
  sourceOptions: SourceTargetOptions;
  targetOptions: TargetOptions;
  transformation: string;
  transformationType: string;
  hideSourceSelection?: boolean;
  onBindingChange: (binding: { sourceFile?: string; sourceField?: string; targetTable?: string; targetField?: string }) => void;
  onApplyTypeFix: (fix: SuggestedFix) => void;
  /** Current edited source fields exposed for ParametersEditor */
  onEditedSourceFieldsChange?: (fields: string[]) => void;
}

const BindingEditor: React.FC<BindingEditorProps> = ({
  mapping,
  mappingResponse,
  sourceOptions,
  targetOptions,
  transformation,
  transformationType,
  hideSourceSelection = false,
  onBindingChange,
  onApplyTypeFix,
  onEditedSourceFieldsChange,
}) => {
  const [bindingEditorOpen, setBindingEditorOpen] = useState(false);
  const [editedSourceFile, setEditedSourceFile] = useState<string | null>(mapping.source_file || null);
  const [editedSourceField, setEditedSourceField] = useState<string | null>(mapping.source_field || null);
  const [editedTargetTable, setEditedTargetTable] = useState<string | null>(mapping.target_table || null);
  const [editedTargetField, setEditedTargetField] = useState<string | null>(mapping.target_field || null);

  // Reset when mapping changes
  React.useEffect(() => {
    setBindingEditorOpen(false);
    setEditedSourceFile(mapping.source_file || null);
    setEditedSourceField(mapping.source_field || null);
    setEditedTargetTable(mapping.target_table || null);
    setEditedTargetField(mapping.target_field || null);
  }, [mapping.mapping_id]);

  const editedAvailableSourceFields = useMemo(() => {
    if (!editedSourceFile) return [];
    return sourceOptions.fieldsByFile[editedSourceFile] ?? [];
  }, [editedSourceFile, sourceOptions]);

  // Notify parent of available source fields for ParametersEditor
  React.useEffect(() => {
    onEditedSourceFieldsChange?.(editedAvailableSourceFields);
  }, [editedAvailableSourceFields, onEditedSourceFieldsChange]);

  const editedAvailableTargetFields = useMemo(() => {
    if (!editedTargetTable) return [];
    return targetOptions.fieldsByTable[editedTargetTable] ?? [];
  }, [editedTargetTable, targetOptions]);

  // Collapsed binding label
  const bindingSourceLabel = useMemo(() => {
    if (!mapping.source_file) return '-- no source --';
    const expr = mapping.transformation || '';
    const knownCols = sourceOptions.fieldsByFile[mapping.source_file] ?? [];
    if (!expr || knownCols.length === 0) {
      return `${mapping.source_file}.${mapping.source_field}`;
    }
    const colSet = new Map(knownCols.map((c) => [c.toLowerCase(), c]));
    const identifiers = expr.match(/\b[A-Za-z_]\w*\b/g) ?? [];
    const primaryLower = (mapping.source_field ?? '').toLowerCase();
    const seen = new Set<string>([primaryLower]);
    const extraCols: string[] = [];
    for (const id of identifiers) {
      const lower = id.toLowerCase();
      if (seen.has(lower)) continue;
      const realName = colSet.get(lower);
      if (realName) {
        seen.add(lower);
        extraCols.push(realName);
      }
    }
    if (extraCols.length > 0) {
      return `${mapping.source_file}: ${mapping.source_field}, ${extraCols.join(', ')}`;
    }
    return `${mapping.source_file}.${mapping.source_field}`;
  }, [mapping, sourceOptions]);

  // Expression reference warning
  const bindingExpressionWarning = useMemo(() => {
    if (!bindingEditorOpen) return null;
    const expr = mapping.transformation || '';
    if (!expr) return null;
    const oldField = mapping.source_field || '';
    const newField = editedSourceField || '';
    if (oldField && newField && oldField !== newField && expr.includes(oldField)) {
      return `Expression references "${oldField}" -- updating source may require updating the expression.`;
    }
    return null;
  }, [mapping, bindingEditorOpen, editedSourceField]);

  // Real-time type compatibility + join validation
  const { validateField, validateJoinPath } = useFieldValidation(mappingResponse);

  const inspectorTypeIssues = useMemo((): TypeCompatibilityIssue[] => {
    if (hideSourceSelection) return [];
    const sourceFile = editedSourceFile || mapping.source_file || '';
    const sourceField = editedSourceField || mapping.source_field || '';
    const targetTable = editedTargetTable || mapping.target_table || '';
    const targetField = editedTargetField || mapping.target_field || '';
    if (!sourceFile || !sourceField || !targetTable || !targetField) return [];
    return validateField({
      sourceFile,
      sourceField,
      targetTable,
      targetField,
      transformation,
      transformationType,
    });
  }, [editedSourceFile, editedSourceField, editedTargetTable, editedTargetField, transformation, transformationType, validateField, mapping]);

  const inspectorJoinWarning = useMemo(() => {
    if (hideSourceSelection) return null;
    const sourceFile = editedSourceFile || mapping.source_file || '';
    const targetTable = editedTargetTable || mapping.target_table || '';
    if (!sourceFile || !targetTable) return null;
    return validateJoinPath(sourceFile, targetTable);
  }, [hideSourceSelection, editedSourceFile, editedTargetTable, validateJoinPath, mapping]);

  // Propagate binding changes to parent on apply
  const handleApply = () => {
    setBindingEditorOpen(false);
    const binding: Record<string, string | undefined> = {};
    if (!hideSourceSelection) {
      if (editedSourceFile && editedSourceFile !== mapping.source_file) binding.sourceFile = editedSourceFile;
      if (editedSourceField && editedSourceField !== mapping.source_field) binding.sourceField = editedSourceField;
    }
    if (editedTargetTable && editedTargetTable !== mapping.target_table) binding.targetTable = editedTargetTable;
    if (editedTargetField && editedTargetField !== mapping.target_field) binding.targetField = editedTargetField;
    onBindingChange(binding);
  };

  const handleCancel = () => {
    setBindingEditorOpen(false);
    setEditedSourceFile(mapping.source_file || null);
    setEditedSourceField(mapping.source_field || null);
    setEditedTargetTable(mapping.target_table || null);
    setEditedTargetField(mapping.target_field || null);
    onBindingChange({});
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, display: 'block' }}>
        Field Connection
      </Typography>
      {!bindingEditorOpen ? (
        <Box
          onClick={() => setBindingEditorOpen(true)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, p: 1, px: 1.5,
            backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider',
            borderRadius: 1, cursor: 'pointer', transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main', backgroundColor: 'primary.50' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600, fontSize: 12, color: 'primary.main' }}
              title={bindingSourceLabel}
            >
              {bindingSourceLabel}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>&#8594;</Typography>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600, fontSize: 12, color: 'success.main' }}
                title={`${mapping.target_table}.${mapping.target_field}`}
              >
                {mapping.target_table}.{mapping.target_field}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}>
            <EditIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </IconButton>
        </Box>
      ) : null}
      {/* Type + join warnings below the binding (collapsed) */}
      {!bindingEditorOpen && (inspectorTypeIssues.length > 0 || inspectorJoinWarning) && (
        <InlineValidationWarnings
          typeIssues={inspectorTypeIssues}
          joinWarning={inspectorJoinWarning}
          onApplyFix={onApplyTypeFix}
          compact
        />
      )}
      {bindingEditorOpen && (
        <Box sx={{ border: '2px solid', borderColor: 'primary.main', borderRadius: 1, p: 1.5, backgroundColor: 'primary.50' }}>
          {!hideSourceSelection && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Source
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.5, mb: 1 }}>
                <Autocomplete
                  size="small"
                  options={sourceOptions.files}
                  value={editedSourceFile}
                  onChange={(_, value) => {
                    setEditedSourceFile(value);
                    setEditedSourceField(null);
                  }}
                  renderInput={(params) => <TextField {...params} label="File / Table" />}
                />
                <Autocomplete
                  size="small"
                  options={editedAvailableSourceFields}
                  value={editedSourceField}
                  onChange={(_, value) => setEditedSourceField(value)}
                  disabled={!editedSourceFile}
                  renderInput={(params) => <TextField {...params} label="Field" />}
                />
              </Stack>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 16, lineHeight: 1 }}>&#8595;</Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>
            </>
          )}

          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Target
          </Typography>
          <Stack spacing={1} sx={{ mt: 0.5, mb: 1.5 }}>
            <Autocomplete
              size="small"
              options={targetOptions.tables}
              value={editedTargetTable}
              onChange={(_, value) => {
                setEditedTargetTable(value);
                setEditedTargetField(null);
              }}
              renderInput={(params) => <TextField {...params} label="Table" />}
            />
            <Autocomplete
              size="small"
              options={editedAvailableTargetFields}
              value={editedTargetField}
              onChange={(_, value) => setEditedTargetField(value)}
              disabled={!editedTargetTable}
              renderInput={(params) => <TextField {...params} label="Field" />}
            />
          </Stack>

          {bindingExpressionWarning && (
            <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ mb: 1.5, py: 0, fontSize: 12 }}>
              {bindingExpressionWarning}
            </Alert>
          )}
          <InlineValidationWarnings
            typeIssues={inspectorTypeIssues}
            joinWarning={inspectorJoinWarning}
            onApplyFix={onApplyTypeFix}
          />
          <Stack direction="row" spacing={1} sx={{ mt: (inspectorTypeIssues.length > 0 || inspectorJoinWarning) ? 1 : 0 }}>
            <Button size="small" variant="contained" onClick={handleApply}>
              Apply
            </Button>
            <Button size="small" variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default BindingEditor;
